import { z } from 'zod/v4'

import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'

import {
  auditCursorSchema,
  auditEventRowSchema,
  boundedCountSchema,
  cursorPageSchema,
} from './audit-common.js'

export const AUDIT_ACTIVITY_DEFAULT_LIMIT = 50
export const AUDIT_ACTIVITY_MAX_LIMIT = 100

const auditWordSchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(/^[a-z][a-z0-9.-]*$/)

/** The filters of the tab ARE its URL: what the URL cannot say the server refuses (strict), and the other way round. */
export const auditActivityQuerySchema = z.strictObject({
  search: z
    .string()
    .trim()
    .min(1)
    .max(200)
    // Postgres text cannot hold NUL: it would be a 500 instead of a 400.
    .refine((text) => !text.includes('\u0000'), 'search cannot contain a NUL character')
    .optional(),
  // The journal's own words (`account.signed-in`, `tax-rule-set`): the server owns the vocabulary.
  action: auditWordSchema.optional(),
  targetType: auditWordSchema.optional(),
  range: z.enum(['today']).optional(),
  cursor: auditCursorSchema.optional(),
  limit: z.coerce
    .number<number>()
    .int()
    .min(1)
    .max(AUDIT_ACTIVITY_MAX_LIMIT)
    .default(AUDIT_ACTIVITY_DEFAULT_LIMIT),
})

export const adminAuditActivityScreen = defineScreen({
  id: 'admin.audit.activity',
  path: '/api/screens/admin/audit/activity',
  access: 'super_admin',
  query: auditActivityQuerySchema,
  response: z.strictObject({
    counts: z.strictObject({
      total: boundedCountSchema,
      /** Since 00:00 UTC. */
      today: boundedCountSchema,
      failedLogins: boundedCountSchema,
    }),
    /** How many rows match the filters; null on every page but the first (a request with `cursor` does not count again). */
    matching: boundedCountSchema.nullable(),
    page: cursorPageSchema(auditEventRowSchema),
    /** The journal's vocabulary: what the action and target filters offer. */
    vocabulary: z
      .strictObject({ actions: z.array(z.string()), targets: z.array(z.string()) })
      .optional(),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.audit'],
  budget: { requests: 1, statements: 3, bytes: 60_000, p95Ms: 150 },
  summary: 'Audit › Activity: bounded counts, one page by cursor and freshness',
})
export type AuditActivityQuery = ScreenQuery<typeof adminAuditActivityScreen>
export type AuditActivityData = ScreenData<typeof adminAuditActivityScreen>
